import { RefObject, useEffect, useRef } from 'react';

import { PopperInstance } from '../Popper.types';

type UsePopperPositionUpdateOptions = {
  anchorRef: RefObject<HTMLElement | null>;
  popperInstanceRef: RefObject<PopperInstance | null>;
  isOpen: boolean;
};

/**
 * Hook to automatically update Popper position when anchor element moves
 *
 * Strategy: Track anchor element's position and force Popper update when it changes
 * This handles cases where anchor moves without scroll events (e.g., content appearing/disappearing)
 */
export const usePopperPositionUpdate = ({ anchorRef, popperInstanceRef, isOpen }: UsePopperPositionUpdateOptions) => {
  const lastPositionRef = useRef<{ top: number; left: number } | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      lastPositionRef.current = null;

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      return;
    }

    const anchorElement = anchorRef.current;

    if (!anchorElement) {
      return;
    }

    // Function to check position and update if changed
    const checkPositionAndUpdate = () => {
      if (!isOpen || !anchorRef.current) {
        return;
      }

      // Check if popperInstance is available yet
      const popperInstance = popperInstanceRef.current;

      if (!popperInstance) {
        // Popper instance not ready yet, keep checking
        animationFrameRef.current = requestAnimationFrame(checkPositionAndUpdate);
        return;
      }

      const rect = anchorRef.current.getBoundingClientRect();
      const currentPosition = { top: rect.top, left: rect.left };

      // Check if position has changed (with small tolerance for sub-pixel changes)
      const tolerance = 0.5;
      const hasChanged =
        !lastPositionRef.current ||
        Math.abs(lastPositionRef.current.top - currentPosition.top) > tolerance ||
        Math.abs(lastPositionRef.current.left - currentPosition.left) > tolerance;

      if (hasChanged) {
        lastPositionRef.current = currentPosition;

        // Try synchronous update first (if available), fallback to scheduleUpdate
        if (popperInstance.update) {
          popperInstance.update();
        } else {
          popperInstance.scheduleUpdate();
        }
      }

      // Continue checking in next frame
      animationFrameRef.current = requestAnimationFrame(checkPositionAndUpdate);
    };

    // Start position tracking (will wait for popperInstance to be ready)
    checkPositionAndUpdate();

    // Cleanup
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      lastPositionRef.current = null;
    };
  }, [anchorRef, popperInstanceRef, isOpen]);
};
